import type {
  ThemeOptions,
  ComponentsProps,
  ComponentsOverrides,
  ComponentsVariants
} from '@mui/material/styles';
import type { Theme } from '@ui/ThemeRegistry/theme.types';

const defaultProps: ComponentsProps['Link'] = {};

const styleOverrides: ComponentsOverrides<Theme>['Link'] = {
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    textAlign: 'center',
    textDecoration: 'none',
    textDecorationColor: 'currentColor',
    boxShadow: 'none'

    // Removing, but possibly for social icons?
    // '.fill-primary': {
    //   fill: `var(--mui-palette-${ownerState?.color ?? 'primary'}-main)`
    // },
    // '.fill-secondary': {
    //   fill: `var(--mui-palette-${
    //     ownerState?.color ? ownerState?.color + '-contrastText' : 'secondary-main'
    //   })`
    // }
  },

  rootButton: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    fontWeight: 900,
    backgroundColor: 'transparent'
  },

  noLinkStyleIcon: ({ theme, iconPosition }) => ({
    margin: iconPosition === 'Left' ? `0 ${theme.spacing(1)} 0 0` : `0 0 0 ${theme.spacing(1)}`
  })
};

const createVariants = (theme: Theme): ComponentsVariants['Link'] => [
  {
    props: {
      variant: 'link'
    },
    style: {
      'textDecoration': 'none',
      'fontWeight': 900,
      'backgroundColor': 'transparent',
      '&:hover': {
        textDecoration: 'none',
        color: 'white'
      }
    }
  },
  {
    props: {
      variant: 'default'
    },
    style: {
      'textDecoration': 'none',
      'fontWeight': 900,
      'backgroundColor': 'transparent',
      '&:hover': {
        textDecoration: 'none',
        color: 'white'
      }
    }
  },
  {
    props: {
      variant: 'buttonText'
    },
    style: {
      textDecoration: 'underline'
    }
  },
  {
    props: {
      variant: 'buttonOutlined'
    },
    style: {
      'color': 'var(--mui-palette-background-contrastText)',
      'borderColor': 'var(--mui-palette-background-contrastText)',

      '&:hover': {}
    }
  },
  {
    props: {
      variant: 'buttonNeonOutline'
    },
    style: {
      ...theme.typography.bodyLarge,
      'position': 'relative',
      'backgroundColor': 'transparent',
      'border': 'none',
      'color': 'var(--mui-palette-background-contrastText)',
      'padding': '.5em',
      'borderRadius': 0,
      'overflow': 'hidden',
      'zIndex': 1,
      '&::before, &:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderWidth: '1px',

        backgroundSize: '200% 200%',
        maskComposite: 'exclude',

        backgroundImage: `radial-gradient(circle at center,
          var(--mui-palette-vibrant-main),
          var(--mui-palette-background-default),
          var(--mui-palette-accent-main),
          var(--mui-palette-background-default),
          var(--mui-palette-vibrant-main))`,
        zIndex: -1,
        animation: 'neonBorderDraw 60s linear infinite'
      },

      '&::before': {
        opacity: 1,
        transition: 'opacity 2s ease-in',
        borderRadius: 0,
        borderColor: 'transparent',
        maskMode: 'match-source',
        maskRepeat: 'no-repeat',
        maskImage: `linear-gradient(to bottom,
          transparent,
          black 0.1em,
          black calc(0.1em + 1px),
          transparent calc(0.1em + 1px),
          transparent calc(100% - 0.1em - 1px),
          black calc(100% - 0.1em - 1px),
          black calc(100% - 0.1em),
          transparent 100%)`
      },

      '&::after': {
        opacity: 0,
        padding: 'var(--mui-shape-borderRadius)',
        transition: 'opacity 2s ease-out',
        borderWidth: '1px',
        borderRadius: 'calc(2 * var(--mui-shape-borderRadius))',
        animation: 'neonBorderDraw 10s linear infinite',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude'
      },

      '&:hover': {
        'color': 'var(--mui-palette-background-contrastText)',

        '&::before': {
          opacity: 0
        },

        '&::after': {
          opacity: 1
        }
      }
    }
  },
  {
    props: {
      variant: 'buttonNeonOutline'
    },
    style: {
      'position': 'relative',
      'textDecoration': 'none',
      'fontWeight': 900,
      'backgroundColor': 'rgba(0, 0, 0, 0.2)',
      'border': 'none',
      'color': '#fff',
      'padding': '12px 24px',
      'borderRadius': '50px',
      'overflow': 'visible',
      'zIndex': 1,
      'transition': 'color 0.3s ease, background-color 0.3s ease, transform 0.2s ease',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '50px',
        padding: '2px',
        background: 'conic-gradient(#8a2be2 0deg, transparent 10deg, transparent 360deg)',
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        zIndex: -1,
        animation:
          'neonBorderDraw 3s cubic-bezier(0.215, 0.61, 0.355, 1) forwards, neonBorderPulse 2s ease-in-out 3s infinite'
      },
      '&:hover': {
        'textDecoration': 'none',
        'color': '#c586ff',
        'backgroundColor': 'rgba(30, 0, 50, 0.2)',
        'transform': 'translateY(-1px)',
        '&::before': {
          animation: 'neonBorderPulse 1.5s ease-in-out infinite',
          background: 'conic-gradient(#8a2be2, #c586ff, #8a2be2, #c586ff, #8a2be2)'
        }
      },
      '&:active': {
        'transform': 'translateY(1px)',
        '&::before': {
          filter: 'brightness(1.2)'
        }
      }
    }
  }
];

export const LinkTheme = (theme: Theme): ThemeOptions => ({
  components: {
    Link: {
      defaultProps,
      styleOverrides,
      variants: createVariants(theme)
    }
  }
});

export default LinkTheme;
